<template>
    <vx-expandable-card>
        <template v-slot:header>
            Workflows
        </template>

        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <vx-alert v-if="success"
                    :closeable="false"
                    color="success"
                    shadow>
                    Updated successfully
                </vx-alert>
                <vx-alert v-if="error"
                    :closeable="false"
                    color="danger"
                    shadow>
                    {{ error }}
                </vx-alert>

                <div>
                    <vx-label size="md">Safe sending hours</vx-label>
                    <div class="text-sm text-slate-500">
                        Workflow messages wait for this daily window in each recipient's local time.
                        Changes apply immediately to all workflows, including published versions.
                    </div>

                    <div class="flex gap-4 mt-3">
                        <div class="grow" data-test="safe-hours-start-time">
                            <vx-label size="sm">Start</vx-label>
                            <vx-selectable
                                size="lg"
                                v-model="startTime"
                                :clearable="false"
                                :options="timeOptions"
                                :reduce="(option) => option.value"
                                :selectable="startSelectable"
                                :error="displayFirstError(errors.safe_hours_start_time)"
                            ></vx-selectable>
                        </div>
                        <div class="grow" data-test="safe-hours-stop-time">
                            <vx-label size="sm">Stop</vx-label>
                            <vx-selectable
                                size="lg"
                                v-model="stopTime"
                                :clearable="false"
                                :options="timeOptions"
                                :reduce="(option) => option.value"
                                :selectable="stopSelectable"
                                :error="displayFirstError(errors.safe_hours_stop_time)"
                            ></vx-selectable>
                        </div>
                    </div>
                </div>

                <vx-button block
                    data-test="update-workflow-settings"
                    size="lg"
                    @click.prevent="submit()"
                    :loading="submitting"
                    :disabled="!dirty || loading">
                    Update
                </vx-button>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxButton, VxExpandableCard, VxLabel, VxAlert, VxSelectable } from '@voxie/frontend-components';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import { nextTick, onMounted, ref, watch } from 'vue';
import { timeOfDayOptions } from '~/utils/date';
import { displayFirstError } from '~/utils/validation';
import { workflowSettingsClient } from '~/services';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

// mirrors the two-hour minimum in UpdateWorkflowSettingsRequest
const MIN_WINDOW_MINUTES = 120;

const timeOptions = timeOfDayOptions();

const minutesOfDay = (time) => {
    const [hours, minutes] = time.split(':').map(Number);

    return hours * 60 + minutes;
};

const startTime = ref('08:00');
const stopTime = ref('20:00');

const startSelectable = (option) => minutesOfDay(option.value) + MIN_WINDOW_MINUTES <= minutesOfDay(stopTime.value);
const stopSelectable = (option) => minutesOfDay(option.value) >= minutesOfDay(startTime.value) + MIN_WINDOW_MINUTES;

const dirty = ref(false);

watch([startTime, stopTime], () => {
    dirty.value = true;
});

const loading = ref(true);
const submitting = ref(false);
const success = ref(false);
const error = ref('');
const errors = ref({});

onMounted(() => {
    workflowSettingsClient.get(props.teamId).then(async (response) => {
        startTime.value = response.data.safe_hours_start_time;
        stopTime.value = response.data.safe_hours_stop_time;
        // the watcher treats this hydration as an edit, so clear it after the flush
        await nextTick();
        dirty.value = false;
        loading.value = false;
    }).catch((e) => {
        console.error(e);
        error.value = 'Something went wrong.';
    });
});

const submit = () => {
    if (submitting.value || loading.value) {
        return;
    }

    success.value = false;
    errors.value = {};
    error.value = '';
    submitting.value = true;

    workflowSettingsClient.update(props.teamId, {
        safe_hours_start_time: startTime.value,
        safe_hours_stop_time: stopTime.value,
    }).then(async (response) => {
        success.value = true;
        startTime.value = response.data.safe_hours_start_time;
        stopTime.value = response.data.safe_hours_stop_time;
        // the watcher treats this hydration as an edit, so clear it after the flush
        await nextTick();
        dirty.value = false;
    }).catch((e) => {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.';
        } else {
            if (e.response.status === HTTP_UNPROCESSABLE_ENTITY) {
                errors.value = e.response.data.errors;
            }
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
    }).finally(() => {
        submitting.value = false;
    });
};
</script>
